import createClient from 'openapi-fetch';

import type { paths } from './schema';
import { apiBaseUrl, type ApiTarget } from './config';
import { authMiddleware } from './middleware/auth';
import { requestIdMiddleware } from './middleware/request-id';
import { errorMiddleware } from './middleware/errors';
import { baseUrlMiddleware } from './middleware/base-url';

/*
 * The single openapi-fetch instance — the one door to the network (spec §3.3).
 * Nothing outside lib/api calls fetch. Cross-cutting concerns live in the
 * middleware chain; order matters: base-url rewrite and request stamping first,
 * error normalization last so it sees the final response.
 *
 * The base URL is resolved through the named `apiConfig` surface (config.ts) via
 * a `target` seam — today there is one backend (`default`), but a future BFF /
 * multi-backend split adds a target there and passes its name here. baseUrl is
 * left relative (`/api`) here; baseUrlMiddleware resolves it to an absolute URL
 * per request so the client works both in the browser (relative ok) and under
 * undici/jsdom in tests (absolute required).
 */
function resolveBaseUrl(base: string): string {
  if (/^https?:\/\//.test(base)) return base;
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  return new URL(base, origin).toString();
}

/**
 * Build an openapi-fetch client for a backend target. Extracted so a future
 * multi-backend split can create a second client for a different `target`
 * without duplicating the middleware chain; `client` below is the single
 * current instance.
 */
function createApiClient(target: ApiTarget = 'default') {
  const instance = createClient<paths>({
    baseUrl: resolveBaseUrl(apiBaseUrl(target)),
    credentials: 'include',
    // Resolve global fetch lazily at call time. openapi-fetch otherwise captures
    // globalThis.fetch at client-creation, which breaks test interception (MSW
    // patches fetch after this module is imported) and any late fetch polyfill.
    fetch: (...args) => globalThis.fetch(...args),
  });

  instance.use(baseUrlMiddleware);
  instance.use(requestIdMiddleware);
  instance.use(authMiddleware);
  instance.use(errorMiddleware);
  return instance;
}

export const client = createApiClient();
